import React from 'react'
import { InjectedFormProps } from 'redux-form'
import { withTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'
import cn from 'classnames'
import i18next from 'i18next'

import MicroActivityBlockStoryForm from './MicroActivityBlockStoryForm'
import CheckMark from 'app/components/SVGS/CheckMark'
import { WhiteCard } from 'app/components/WhiteCard/WhiteCard'
import { IMicroActivityBlockProps } from './MicroActivityBlock.interface'
import Text from 'app/components/Text'
import ReplaceIcon from 'app/components/SVGS/ReplaceIcon'
import SocialPopoverWithButton from '../SocialPopoverWithButton'
import ActivityImageOrActivityVideo from '../ActivityImageOrActivityVideo'
import Button from 'app/components/ButtonDefault/ButtonDefault'
import { SELECTION } from 'app/constants/stages'
import Pulse from 'app/components/SVGS/Pulse'
import { DIFFERENT_ACTIVITY, SELECT_PRO_TRACK } from 'app/domains/routes/constants'
import { EDIT_STORY_FORM_NAME, CREATE_STORY_FORM_NAME } from 'app/constants/activities'

import './styles.scss'

const bc = 'micro-activity-block'
const transNS = 'common'

const DoneActivityButtonIcon = () => (
  <div className={`${bc}__icon`}>
    <CheckMark />
  </div>
)

export const onboardingId = 'todayActivity'

export const getIdForButton = (buttonTitle: string) => {
  return buttonTitle === i18next.t('user:I Did it') || buttonTitle === i18next.t('user:I commit')
    ? onboardingId
    : undefined
}

const MicroActivityBlock: React.FC<IMicroActivityBlockProps & InjectedFormProps<{}, IMicroActivityBlockProps>> = ({
  title,
  activityHeader,
  cardDescription,
  buttonTitle,
  activityImg,
  editOrCreateStory,
  className,
  isCommitStage,
  activityVideoId,
  activityVideoThumbnail,
  showShare,
  handleSubmit,
  whiteCardClassName,
  stage,
  titleIcon,
  showTitle = true,
  isUpdating,
  t,
  fromZeroStepOfTour,
  demo,
  isTodayActivityNull,
  isPreviousActivity,
  ...props
}) => {
  const history = useHistory()

  const disableButton = !handleSubmit
  const isStageSelection = stage === SELECTION

  const renderActivityBlock = () => {
    return (
      <>
        <div className={`${bc}__wrapper-activity`}>
          {showTitle && (
            <div className={`${bc}__title-wrapper`}>
              <Text htmlTag="p" customTitleClass={`${bc}__title`}>
                {title} {titleIcon}
              </Text>
              <div className={`${bc}__title-buttons-wrapper`}>
                {isCommitStage && (
                  <div
                    className={`${bc}__btn-icon-wrapper`}
                    onClick={() => history.push(`/${DIFFERENT_ACTIVITY}/future`)}
                  >
                    <span className={`${bc}__btn-title`}>{t('Different Activity')}</span>
                    <ReplaceIcon />
                  </div>
                )}
                {showShare && <SocialPopoverWithButton wrapperClass={`${bc}__social-share`} />}
              </div>
            </div>
          )}
          <div>
            {(activityImg || activityVideoId) && (
              <div className={`${bc}__activity-img-wrapper`}>
                <ActivityImageOrActivityVideo
                  videoId={activityVideoId as string}
                  videoThumbnail={activityVideoThumbnail as string}
                  imageSrc={activityImg}
                  height={180}
                  demo={demo}
                />
              </div>
            )}
            <Text htmlTag="h4" customWrapperClass={`${bc}__header-wrapper`}>{`${activityHeader}`}</Text>
            <p
              className={cn(`${bc}__description`, {
                [`${bc}__description-story`]: editOrCreateStory,
                [`${bc}__description--demo`]: demo,
              })}
              dangerouslySetInnerHTML={{ __html: cardDescription }}
            />
            {editOrCreateStory && (
              <MicroActivityBlockStoryForm
                {...props}
                t={t}
                form={isPreviousActivity ? EDIT_STORY_FORM_NAME : CREATE_STORY_FORM_NAME}
                isPreviousActivity={isPreviousActivity}
              />
            )}
          </div>
          <Button
            className={`${bc}__button`}
            disabled={disableButton || isUpdating}
            onClick={handleSubmit}
            submitting={isUpdating}
            id={getIdForButton(buttonTitle)}
            primary={fromZeroStepOfTour}
          >
            {t(buttonTitle)}
            {false && <DoneActivityButtonIcon />}
          </Button>
        </div>
      </>
    )
  }

  const renderNoActivityBlock = () => {
    return (
      <>
        <div className={`${bc}__title-wrapper`}>
          <Text htmlTag="p" customTitleClass={`${bc}__title`}>
            {t('Daily Activity')}
          </Text>
          <div className={`${bc}__title-buttons-wrapper`}>
            <div className={`${bc}__btn-icon-wrapper`} onClick={() => history.push(`/${SELECT_PRO_TRACK}`)}>
              <span className={`${bc}__btn-title`}>{t('Switch Protrack')}</span>
              <ReplaceIcon />
            </div>
          </div>
        </div>

        <div className={`${bc}__activity-img-wrapper`}>
          <Pulse className={`${bc}__activity-img-wrapper-noActivity`} />
        </div>

        <Text htmlTag="h4" customWrapperClass={`${bc}__header-wrapper ${bc}__noActivity`}>
          {t(`You have no Microactivities`)}
        </Text>
        <p className={cn(`${bc}__description ${bc}__noActivity ${bc}__description-noActivity`)}>
          {t('Switch and start a new Protrack to receive your Daily Microctivities.')}
        </p>

        <Button
          className={`${bc}__button`}
          onClick={() => history.push(`/${SELECT_PRO_TRACK}`)}
          id={'switch-protrack'}
          outlineSecondary
        >
          {t('Switch Protrack')}
        </Button>
      </>
    )
  }

  return (
    <div
      className={cn(bc, { [`${className}`]: className, [`${bc}--demo`]: demo })}
      id={editOrCreateStory ? 'storyBlock' : undefined}
    >
      <WhiteCard
        className={cn(`${bc}__card`, { [`${whiteCardClassName}`]: whiteCardClassName, [`${bc}__card--demo`]: demo })}
      >
        {isStageSelection || isTodayActivityNull ? renderNoActivityBlock() : renderActivityBlock()}
      </WhiteCard>
    </div>
  )
}

export default withTranslation(transNS)(MicroActivityBlock)
